import { Component, ElementRef, Input, NgZone, OnInit, ViewChild } from "@angular/core";
import { Campo } from "../../models/coleta";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { AppValidators } from "../dynamic-inputs/util/validators";
import { Form } from "../../models/app-state";
import { AppFlowService } from "../../services/app-flow.service";
import { NgxIndexedDBService } from "ngx-indexed-db";
import { MatDialog } from "@angular/material/dialog";
import { DialogComponent } from "../dialog/dialog.component";
import { SessionStorageService } from "src/app/services/session-storage.service";
import {
  CameraTipo,
  cameraTipoToString,
} from "src/app/models/camera-tipo.enum";
import { CampoUpdate } from "src/app/models/campo-model-update";
import { convertBlobToBase64 } from "src/app/utils/base64";
import { FortFaceData, FortFaceDataDocument } from "src/app/models/fortface-data";

@Component({
  selector: "app-image-capture-input",
  templateUrl: "./image-capture-input.component.html",
  styleUrls: ["./image-capture-input.component.scss"],
})
export class ImageCaptureInputComponent implements OnInit {
  @Input()
  field!: Campo;

  @Input()
  formGroup!: UntypedFormGroup;

  @Input()
  form!: Form;

  @Input()
  isSelf = false;

  sideName!: string;

  @Input()
  hideSideName = false;

  captureOpen = false;

  images: any = {};

  inputLoaded: Boolean = false;

  permiteAnexo: boolean = false;

  isImagemAnexadaPdf: boolean = false;

  parametroCameraIntegrada: string = "Default";

  usarFortfaceParaDocumento: boolean = false;

  @ViewChild('inputUploadArquivo')
  inputUploadArquivo?: ElementRef<HTMLInputElement>;

  constructor(
    private appFlowService: AppFlowService,
    private dbService: NgxIndexedDBService,
    public dialog: MatDialog,
    private sessionStorageService: SessionStorageService,
    private ngZone: NgZone
  ) { }

  async ngOnInit(): Promise<void> {
    this.carregaParametroCameraIntegracao();

    //OITI
    //Atualiza todos os componentes de input após a captura, visto que na OITI existe multicapture.
    this.appFlowService.reloadComponent.subscribe((images) => {

      //Aplica a logica de carregar imagem apenas para os componentes que houve captura de imagem. Ex: pessoa.cnh.frente/pessoa.cnh.verso
      if (images.some((image) => this.field.codigo === image.image.type)) {
        this.carregaImagens();
        this.updateFormControl();
      }
    });

    this.atualizaPermissoesAnexo();

    await this.carregaImagens();
  }

  private atualizaPermissoesAnexo() {
    if (!this.field.atributos
      || !this.field.atributos.some(atributo => atributo.propriedade === "MODO_ENTRADA")) return;

    const atributo = this.field.atributos.find(atributo => atributo.propriedade === "MODO_ENTRADA");

    this.permiteAnexo = atributo && atributo.valor && atributo.valor.includes("anexo") ? true : false;
  }

  private updateFormControl() {
    const codigo = this.field?.codigo;

    if (codigo) {
      const formControl = new UntypedFormControl(
        codigo,
        AppValidators.getValidations(this.field)
      );
      this.formGroup.removeControl(codigo);
      this.formGroup.addControl(codigo, formControl);
    }
  }

  private async carregaImagens() {
    this.sideName = this.field.titulo || "";
    this.images = await this.appFlowService.getAllImages().then((images) =>
      images.reduce((acc: any, item: any) => {
        return { ...acc, [item.id]: item.base64 };
      }, {})
    );
    const codigo = this.field?.codigo;
    if (codigo) {
      let formControl = this.formGroup.controls[codigo];
      if (!formControl) {
        const value = this.images[codigo] ? codigo : undefined;
        formControl = new UntypedFormControl(
          value,
          AppValidators.getValidations(this.field)
        );
        this.formGroup.addControl(codigo, formControl);
      }

      //Atualiza a imagem no formulário do angular caso o formControl nao tenha valor e a imagem esteja salva no localStorage.
      //Bugfix para casos de atualizacao da página.
      if (this.images[codigo] && formControl && !formControl.value) {
        this.dbService.delete("images", codigo);
        delete this.images[codigo];
      }

    }

    if (this.field.codigo) {
      this.appFlowService.getImageBase64(this.field.codigo).then(campoCaptura => {
        if (!campoCaptura) return;
        this.isImagemAnexadaPdf = campoCaptura.isPdf;
      });
    }
  }

  get hasImage(): boolean {
    return this.images[this.field.codigo || ""];
  }

  openCapture(): void {
    this.isImagemAnexadaPdf = false;
    if (!this.hasImage) {
      this.captureOpen = true;
    }
  }

  removeImage(event: Event): void {
    event.stopPropagation();
    const codigo = this.field?.codigo;
    if (codigo) {
      this.dbService.delete("images", codigo);
      delete this.images[codigo];
      this.formGroup.controls[codigo].setErrors({ incorrect: true });
      this.isImagemAnexadaPdf = false;
      // this.formGroup.removeControl(codigo);
    }
  }

  anexarCaptura() {
    if (!this.inputUploadArquivo) return;
    this.inputUploadArquivo.nativeElement.click();
  }

  onUploadFile(event: any) {
    this.isImagemAnexadaPdf = false;

    if (!event.target || !event.target.files) return;

    const image: Blob = event.target.files[0];

    if (!this.isFormatoValido(image)) return;

    convertBlobToBase64(image).then(base64Convertido => {
      if (!base64Convertido) return;
      this.onImageCapture({ image: base64Convertido })
    });
  }

  private isFormatoValido(image: Blob): boolean {
    const tamanhoMaximoPermitido = this.sessionStorageService.getEnvConfig().tamanhoMaximoAnexo;

    if (tamanhoMaximoPermitido && tamanhoMaximoPermitido > 0 && image.size > tamanhoMaximoPermitido) {
      this.openDialog("Oooops!", `O tamanho máximo permitido para anexo é de ${tamanhoMaximoPermitido / 1000} KB`);
      return false;
    }

    if ((image.type && (!image.type.includes("image") && image.type !== "application/pdf")) || !image.type) {
      this.openDialog("Oooops!", "O formato de arquivo escolhido não é válido!");
      return false;
    }

    if (image.type === "application/pdf") {

      if (!this.field.atributos ||
        (this.field.atributos && !this.field.atributos.some(
          atributo => atributo.propriedade === "MODO_ENTRADA" && atributo.valor.toLocaleLowerCase() === "anexo_pdf")
        )) {

        this.openDialog("Oooops!", "Este campo não permite anexos de PDF.");
        return false;
      }

      this.isImagemAnexadaPdf = true;
    }

    return true;
  }

  onImageCapture(data: { image: string; appKey?: string }): void {
    const base64 = data.image;
    const appKey = data.appKey;
    const id = this.field.codigo || "";
    const isPdf = this.isImagemAnexadaPdf;
    this.dbService.update("images", { base64, id, isPdf }).subscribe((item: any) => {
      this.captureOpen = false;
      this.images[id] = base64;

      this.updateFormControl();
      //OITI
      //Caso venha appKey da OTI via parametro, atualiza o biometria.consulta
      if (appKey) {
        if (appKey === "") return;

        const biometria = { motor: "OITI", id: appKey };

        this.formGroup.patchValue({
          "biometria.consulta": JSON.stringify(biometria),
        });
      }

    });


  }

  //OITI
  //Inicialmente feito para OITI, visto que existe o multicapture.
  onMultipleImageCapture(
    images: { image: { url: string; type: string } }[]
  ): void {
    images.forEach((images) => {
      const base64 = images.image.url;
      const id = images.image.type || "";
      this.dbService.update("images", { base64, id }).subscribe((item: any) => {
        this.captureOpen = false;
        this.images[id] = base64;
        const codigo = images.image.type;
        if (codigo) {
          const formControl = new UntypedFormControl(codigo);
          this.formGroup.removeControl(codigo);
          this.formGroup.addControl(codigo, formControl);
        }
      });
    });
  }

  handleSerasaUnificadoLivenessCapture(allowMeData: any): void {
    this.ngZone.run(() => {
      const base64 = allowMeData.image;
      const id = this.field.codigo || "";
      const isPdf = this.isImagemAnexadaPdf;

      this.dbService.update("images", { base64, id, isPdf }).subscribe((item: any) => {
        this.captureOpen = false;
        this.images[id] = base64;

        this.updateFormControl();

        this.formGroup.patchValue({
          'pessoa.face.jwt': allowMeData.jwt,
        });
      });
    });
  }

  handleFortfaceCapture(fortFaceData: FortFaceData): void {
    this.ngZone.run(() => {

      const base64 = fortFaceData.imagePreview;
      const id = this.field.codigo || "";
      const isPdf = this.isImagemAnexadaPdf;
      this.dbService.update("images", { base64, id, isPdf }).subscribe((item: any) => {
        this.captureOpen = false;
        this.images[id] = base64;

        this.updateFormControl();

        this.formGroup.patchValue({
          'fortface.sessionId': fortFaceData.sessionId,
          'fortface.data': fortFaceData.data,
          'fortface.imgData': fortFaceData.imgData,
          'fortface.key': fortFaceData.key
        });

      });
    });
  }

  handleFortfaceCaptureDocument(fortFaceData: FortFaceDataDocument) {
    this.ngZone.run(() => {

      const base64 = fortFaceData.imagePreview;
      const id = this.field.codigo || "";
      const isPdf = this.isImagemAnexadaPdf;

      this.dbService.update("images", { base64, id, isPdf }).subscribe((item: any) => {
        this.captureOpen = false;
        this.images[id] = base64;

        this.updateFormControl();

        if (fortFaceData.isLastCtps) {
          this.formGroup.patchValue({
            'fortface.documento.ctps.lastRegister.sessionId': fortFaceData.sessionId,
            'fortface.documento.ctps.lastRegister.data': fortFaceData.data,
            'fortface.documento.ctps.lastRegister.imgData': fortFaceData.imgData,
            'fortface.documento.ctps.lastRegister.key': fortFaceData.key
          });
          return;
        }

        if (fortFaceData.isVerso) {
          this.formGroup.patchValue({
            'fortface.documento.verso.sessionId': fortFaceData.sessionId,
            'fortface.documento.verso.data': fortFaceData.data,
            'fortface.documento.verso.imgData': fortFaceData.imgData,
            'fortface.documento.verso.key': fortFaceData.key
          });
          return
        }

        this.formGroup.patchValue({
          'fortface.documento.sessionId': fortFaceData.sessionId,
          'fortface.documento.data': fortFaceData.data,
          'fortface.documento.imgData': fortFaceData.imgData,
          'fortface.documento.key': fortFaceData.key
        });
      });
    });
  }

  onPatchValue(campoUpdate: CampoUpdate) {
    if (campoUpdate) {
      if (campoUpdate.campo === "") return;

      const campo = `${campoUpdate.campo}.jwt`;
      const valor = campoUpdate.valor;

      this.formGroup.patchValue({
        [campo]: valor,
      });
    }
  }

  onCancel(event: Event): void {
    this.captureOpen = false;
    this.inputLoaded = false;
    this.isImagemAnexadaPdf = false;
    this.removeImage(event);
  }

  onErrorCapture(mensagem: string) {
    this.captureOpen = false;
    this.inputLoaded = false;
    this.openDialog("Erro!", mensagem);
  }

  get backgroundImage(): string {
    return this.images[this.field?.codigo || ""];
  }

  openDialog(title: string, message: string): void {
    const dialogRef = this.dialog.open(DialogComponent, {
      width: "250px",
      data: { title: title, message: message },
    });
  }

  onLoaded(loaded: Boolean) {
    this.inputLoaded = true;
  }

  carregaParametroCameraIntegracao() {

    this.usarFortfaceParaDocumento = !!this.sessionStorageService.getEnvConfig().usarFortfaceParaDocumentos;

    //Busca o parametro no local storage
    const cameraTipo: CameraTipo | undefined =
      this.sessionStorageService.getEnvConfig().cameraTipoIntegracao;

    //Se o parametro nao existir, retorna.
    if (!cameraTipo) return;

    //Se o parametro estiver ativo e o valor estiver valido, atribuimos o valor.
    if (
      Object.values(CameraTipo)
        .map((cameraTipo) => cameraTipo.toString().toLocaleLowerCase())
        .includes(cameraTipo.toLocaleLowerCase())
    ) {
      this.parametroCameraIntegrada = cameraTipo.toLocaleLowerCase();
      return;
    }

    //Se o parametro estiver ativo e o valor estiver invalido, lançamos um erro.
    throw new Error(`
      Valor do parametro cameraTipoIntegracao não é válido!
      Favor utilizar um dos valores abaixo:
      ${cameraTipoToString()}
      `);
  }
}
